"use client";

import { c as _c } from "react/compiler-runtime";
import { AuthFlow } from "@/components/auth/auth-flow";
import { AuthFlowHeader } from "@/components/auth/auth-flow-header";
import { PhoneMethod } from "@/components/auth/methods/phone";
import { PhoneAuthStep, usePhoneAuth } from "@/components/auth/methods/phone/phone-auth-context";
import { AuthMethodId, AuthSuccess, AuthSurface } from "@/components/auth/types";
import { Button } from "@/components/ui";
import { EmailPasswordPhoneEntry } from "./email-password-phone-entry";
import { SignUpAuthProvider } from "./sign-up-auth";
import { patientSourceForSurface } from "./sign-up-source";
import { SignUpSteps } from "./sign-up-steps";
import { PendingGoogleSignUp } from "./use-google-sign-up-continuation";
function CancelSignUpButton(t0) {
  const $ = _c(3);
  const {
    onCancel
  } = t0;
  const {
    state
  } = usePhoneAuth();
  let t1;
  if ($[0] !== onCancel || $[1] !== state.isClaiming) {
    t1 = <Button type="button" variant="ghost" className="mt-4 w-full" onClick={onCancel} disabled={state.isClaiming}>Use a different sign-in method</Button>;
    $[0] = onCancel;
    $[1] = state.isClaiming;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}
export interface GoogleSignUpContinuationProps {
  pending: PendingGoogleSignUp;
  /** Where the surface goes once the account is linked. */
  onSuccess: (credential: AuthSuccess) => Promise<void> | void;
  /** Back out to the surface's sign-in options. */
  onCancel: () => void;
  /** Surface the Google sign-in ran on, for patient source and attribution. */
  originSurface: AuthSurface;
}

/**
 * Sign-up, continued in place on whichever surface the Google sign-in ran.
 *
 * A patient who taps "Sign in with Google" without an account at this clinic
 * gets the rest of sign-up here rather than a "no account" error: verify the
 * phone number, match or create the patient record, and link it to the Google
 * identity they already authenticated with. The captured credential re-enters
 * that Google session at the end, so the popup only ever opens once.
 */
export function GoogleSignUpContinuation(t0) {
  const $ = _c(19);
  const {
    pending,
    onSuccess,
    onCancel,
    originSurface
  } = t0;
  const {
    entry,
    email
  } = pending;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [AuthMethodId.PHONE];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== originSurface) {
    t2 = patientSourceForSurface(originSurface);
    $[1] = originSurface;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const t3 = email ? `We don't have an account for ${email} at this clinic yet. Enter your phone number and we'll text you a code to verify it's you.` : "We don't have an account for that Google profile at this clinic yet. Enter your phone number and we'll text you a code to verify it's you.";
  let t4;
  if ($[3] !== t3) {
    t4 = <AuthFlowHeader className="mb-6 sm:mb-8" showOnlyWhilePhoneStep={PhoneAuthStep.ENTER_PHONE} title="Finish setting up your account" description={t3} />;
    $[3] = t3;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  let t6;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <EmailPasswordPhoneEntry />;
    t6 = <SignUpSteps />;
    $[5] = t5;
    $[6] = t6;
  } else {
    t5 = $[5];
    t6 = $[6];
  }
  let t7;
  if ($[7] !== onCancel) {
    t7 = <CancelSignUpButton onCancel={onCancel} />;
    $[7] = onCancel;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== t4 || $[10] !== t7) {
    t8 = <PhoneMethod className="p-0">{t4}{t5}{t6}{t7}</PhoneMethod>;
    $[9] = t4;
    $[10] = t7;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== entry || $[13] !== t2 || $[14] !== t8) {
    t9 = <SignUpAuthProvider googleEntry={entry} patientSource={t2}>{t8}</SignUpAuthProvider>;
    $[12] = entry;
    $[13] = t2;
    $[14] = t8;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== onSuccess || $[17] !== t9) {
    t10 = <AuthFlow surface={AuthSurface.PATIENT_SIGN_UP} defaultMethod={AuthMethodId.PHONE} enabledMethods={t1} onSuccess={onSuccess}>{t9}</AuthFlow>;
    $[16] = onSuccess;
    $[17] = t9;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  return t10;
}